import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'
import { DAIMI_BK_SLUG } from '@/content/daimiBanking'

/**
 * The Future 2030 Banking outlook page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **The sixth Future 2030 outlook with a designed page of its own**, and the
 * fifteenth report overall. Asked for on 2026-08-28, in the same words as the
 * Automotive, Manufacturing, Consumer Goods, Retail and Pharmaceutical ones —
 * *"referans alarak ilgili sayfayı birebir tıpa tıp aynısını tasarla"* — as a
 * four-part full-page capture of digitopia.co's Banking outlook page, plus the six
 * images that page carries: the coin-stack-on-a-circuit-board cover art, the DMI
 * forecast chart, the three requirement photographs and the two-copy report
 * mockup. And, as on the Consumer Goods, Retail and Pharmaceutical requests, the
 * instruction that the maturity band point at the 2026 DAIMI report rather than the
 * 2024 one the live page offers.
 *
 * So the bands are the reference's, in the reference's order: hero → the forecast
 * beside Figure 1 → a dark band of three imperatives → the five predictions → the
 * pointer at the maturity report → the download close. The two shared closing bands
 * (`content/reportClosing.ts`) still sit where they sit on every other report page.
 *
 * **The shape is the Retail page's**: two lead-in lines over the predictions, a dark
 * band whose second line is a title rather than a second claim, and five plain
 * statements rather than label-and-figure pairs. Two things are this page's own. Its
 * forecast band has **no closing line** — the live band goes straight from the third
 * trajectory to the Download button, the way the Pharmaceutical one does. And its
 * close is the live page's own, "Download the Full Report", rather than the
 * invitation the Retail page closes with.
 *
 * **One sector slip in the reference, corrected here and flagged.** The live page's
 * maturity CTA reads "Explore the 2024 **Retail** Digital Maturity Report" under a
 * paragraph about the *Banking* one — the same leftover the Pharmaceutical page
 * carries, and the same evidence that these pages were built from the Retail one.
 * Transcribing it literally would put another sector's name on a banking page, so it
 * is written for banking here and the band's `check` says what the reference said.
 *
 * **Two deliberate departures from the capture**, both the settled house call rather
 * than a reading of the reference:
 *
 *  * the h1 is the series headline every outlook page here uses ("Digital
 *    Transformation Outlook for Banking") rather than the live page's "Future 2030:
 *    Banking Industry Outlook" — the eyebrow above it already says Future 2030, and
 *    the six outlook pages read as one series this way.
 *  * the maturity band carries the 2026 Banking Industry Focus cover, which the live
 *    band does not. `OUTLOOK_BK_MATURITY.check` is where that is asked about, because
 *    it is one of the two places this page shows something the reference has not.
 *
 * **Where the words come from.** Everything here is transcribed from the four
 * captures, which are full-page renders read down to fit — the *shape* of the page
 * is certain and the *wording* is a careful reading of body copy set a handful of
 * pixels tall. Every band that carries prose therefore carries a `check`, the same
 * way `content/outlookRetail.ts` does, and `outlookBankingChecks()` feeds them into
 * the same manifest so a production build cannot ship them unread.
 *
 * **What the live page has that this page cannot.** Its Download buttons and its
 * closing band open an embedded HubSpot form — first and last name, business email,
 * company, job title, phone, country, industry, a marketing opt-in and the privacy
 * statement. These documents are prerendered with no client bundle at all
 * (`src/prerender.tsx`), so a form script would never run, and the other thirty
 * reports on this site hand the reader the PDF from our own bucket instead. Every
 * Download button here is that PDF, and `OUTLOOK_BK_CLOSE.check` is what keeps the
 * gating question open.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The short slug digitopia.co uses, and the one the request named. It was already in
 * `REPORTS` before this page existed, so the page is built under it as it stands
 * rather than moved to a `…-outlook-2030` one.
 */
export const OUTLOOK_BK_SLUG = 'banking-industry-outlook'

/*
 * The Banking Industry Focus cut of the Digital & AI Maturity Report 2026 is where
 * this page's maturity band sends the reader. Its slug is **imported** rather than
 * written out — unlike the Retail and Pharmaceutical pages', which have to repeat a
 * literal from `content/reports.ts` because their maturity cut is drawn by the shared
 * template. The Banking cut has a designed page of its own
 * (`content/daimiBanking.ts`), so it exports `DAIMI_BK_SLUG` and there is no second
 * copy to keep in step.
 */

/**
 * The hero.
 *
 * `lede` is set bold above the paragraph because that is how the live hero sets it —
 * one claim in bold, the reason for it underneath — and the button says "Download the
 * Report" rather than the "Download Report" every other band uses, which is also the
 * live page's wording.
 */
export const OUTLOOK_BK_INTRO = {
  lede: 'Traditional Banks May Not Survive, but Banking Will',
  body: 'Digital disruption is rewriting the rules of finance. Consumers want speed, personalization, and seamless experiences, whether from a bank, a fintech, or a tech giant. As we head toward 2030, banks must evolve or risk irrelevance.',
  downloadLabel: 'Download the Report',
  /**
   * The cover art supplied with this request — the stack of coins on a circuit
   * board, which is the artwork behind the report cover with none of its lettering
   * on it. This is where the live hero puts it too, at the same size and on the same
   * side.
   */
  photo: {
    src: '/photo/future-2030-banking-digital-transformation.webp',
    alt: 'A stack of gold coins standing on a glowing blue circuit board',
  } satisfies Photo,
  check:
    'The hero’s two lines are transcribed from the reference capture rather than supplied as text. Read them back against digitopia.co before production. The image, its side and its size are the live page’s.',
}

/**
 * The forecast band: Figure 1 on the left, the three trajectories on the right.
 *
 * The three are one block on the live page in which each line opens with a bolded
 * label — "Leaders", "Average banks", "Laggards". Split into `label` and `body` here
 * so the label can be set rather than emphasised inside a sentence, which is the call
 * every designed report page on this site has made.
 *
 * No closing line: like the Pharmaceutical band and unlike the Retail one, the live
 * band goes straight from the third trajectory to the Download button.
 */
export const OUTLOOK_BK_FIGURE = {
  heading: 'Banking Digital Maturity Forecast to 2030',
  intro: 'Digitopia’s latest DMI data shows a stark divide:',
  points: [
    {
      label: 'Leaders',
      body: 'Poised to reach 4.66 by 2029, dominating the industry through AI-first strategies, customer obsession, and financial discipline.',
    },
    {
      label: 'Average banks',
      body: 'Could stagnate around 3.36–3.46, stable but unremarkable.',
    },
    {
      label: 'Laggards',
      body: 'Will struggle to pass 2.70 unless they act now to reinvent.',
    },
  ],
  /**
   * Figure 1, the file supplied with this request: five years of actuals (2020–24)
   * and five of forecast (2025–29), the leader, average and laggard trajectories
   * each fanning out into an optimistic, moderate and pessimistic band. It is
   * 1200×784 on a white ground, so it gets a white card of its own and scales to it
   * whole — ten year columns with three labelled values each do get small on a
   * phone, but a figure the reader has to drag sideways reads as broken.
   */
  photo: {
    src: '/photo/future-2030-banking-dmi-forecast.webp',
    alt: 'Banking DMI evolution 2020 to 2029: actual leader, average and laggard scores to 2024, then optimistic, moderate and pessimistic forecast bands to 2029',
  } satisfies Photo,
  caption: [
    'Figure 1: Banking Industry DMI Evolution (2020–2029) with Scenario-Based Forecasts',
    'Leader, Average, and Laggard Trajectories under Optimistic, Moderate, and Pessimistic Projections',
  ],
  check:
    'Figure 1 is the file you supplied and is shown whole. Three readings wanted on the words beside it. The leaders’ 4.66 by 2029 is exactly the chart’s optimistic leader label, and the laggards’ “struggle to pass 2.70” sits right on its 2.72 pessimistic one. The average band is the one that does not quite line up: the page says 3.36–3.46 while the chart’s 2029 average labels are 3.36 pessimistic, 3.43 moderate and 3.64 optimistic — so either the top of that range should read 3.43, or it is quoting a figure from the report that the chart does not show. Say which. And the live page calls the index DMI here while every other page on this site calls it DAIMI, the Digital & AI Maturity Index; the chart itself is lettered DMI, so both were left as they are — say which name this page should use.',
}

/**
 * The dark imperatives band: two headline lines, then three cards.
 *
 * The live band's second line is not a sentence but a title — "Three Strategic
 * Imperatives to Win in Banking" — so it is set lighter and larger under the headline
 * rather than as a second claim, which is what the component already does with it.
 */
export const OUTLOOK_BK_REQUIREMENTS = {
  heading: 'Banking’s Digital Revolution Has Arrived',
  subheading: 'Three Strategic Imperatives to Win in Banking',
  /** The eyebrow above each card's title, numbered in the order the live band has. */
  eyebrow: 'How to Get There',
  items: [
    {
      title: 'Customer Obsession, No Exceptions',
      body: 'Build frictionless, hyper-personalized experiences across every channel. Loyalty will follow.',
      linkLabel: 'Digital Solutions',
      linkHref: LINKS.solutions,
      photo: {
        src: '/photo/banking-customer-obsession.webp',
        alt: 'A smiling woman in a café holding a payment card in front of her laptop',
      } satisfies Photo,
    },
    {
      title: 'Operate in an Agentic AI World',
      body: 'Use autonomous AI to handle operations, predict customer needs, and manage compliance proactively.',
      linkLabel: 'Artificial Intelligence',
      linkHref: LINKS.enterpriseAi,
      photo: {
        src: '/photo/banking-agentic-ai-world.webp',
        alt: 'A backlit blue keyboard with a bank building icon glowing on one of its keys',
      } satisfies Photo,
    },
    {
      title: 'Build a Future-Ready Workforce',
      body: 'Upskill your teams to lead with data, adapt with agility, and co-create value in an AI-powered world.',
      linkLabel: 'Digital Upskilling',
      linkHref: LINKS.transformation,
      photo: {
        src: '/photo/banking-future-ready-workforce.webp',
        alt: 'A bank adviser shaking hands with two customers across her desk',
      } satisfies Photo,
    },
  ],
  check:
    'Two things. The two headline lines and the three card texts are transcribed from the reference capture. And the third card’s link is the one destination that had to change: the live page sends “Digital Upskilling” to a solution page digitopia.co has and this site does not — it came across in the import as a rebuild source rather than as a page, so linking to it would be a dead link. It points at Transformation Management until that page is built. The other two are the live destinations. This is the same substitution the Automotive, Manufacturing, Consumer Goods, Retail and Pharmaceutical outlook pages make, and one answer settles all six.',
}

/**
 * The predictions band, with the report mockup beside it.
 *
 * Five plain statements rather than the Automotive page's label-and-figure pairs,
 * because that is what the live band prints. Two lead-in lines rather than one: the
 * live band states that the disruption is systemic before it introduces the list.
 */
export const OUTLOOK_BK_PREDICTIONS = {
  heading: 'Five Predictions for the 2030 Banking Landscape',
  intro: [
    'The next wave of disruption in banking won’t be optional; it will be systemic.',
    'As we approach 2030, these five predictions highlight how technology, customer behavior, and industry structure will fundamentally reshape the role of banks in society.',
  ],
  items: [
    'Workforce shrinks by 30% due to hyper-automation',
    'Banks become proactive, AI-driven financial assistants',
    'Banking is embedded into daily life, not accessed separately',
    'Massive consolidation leads to a few dominant digital players',
    'Compliance and cybersecurity are fully automated',
  ],
  /** The mockup supplied with this request — the Future 2030 cover, not a maturity cut's. */
  photo: {
    src: '/photo/future-2030-outlook-banking-mockup.webp',
    alt: 'Two copies of Future 2030: Digital Transformation Outlook for Banking',
  } satisfies Photo,
  check:
    'The heading, both lead-in lines and all five predictions are transcribed from the reference capture. Only the first prediction carries a figure — a 30% workforce reduction — and that one is a claim from the report rather than anything Figure 1 shows, so it wants confirming rather than checking. The mockup beside them is the file you supplied; the reference capture shows that half of the band empty, so say if the live page really carries no image there and it should come out. This is the same call the Retail page made.',
}

/**
 * The band pointing at the maturity report.
 *
 * **The one band whose content is deliberately not the live page's, and the only
 * change in this request that was asked for in words rather than shown in a
 * picture.** digitopia.co sends the reader to a *2024* report; the instruction with
 * the captures was *"en aşağıda önceden konuştuğumuz gibi 2024'e değil 2026 DAIMI
 * report'a yönlendirelim"*. So the CTA, the cover and the sentence all name the
 * Banking Industry Focus cut of the Digital & AI Maturity Report 2026, which is the
 * report this site actually publishes — and which already has a designed page here.
 *
 * That is settled, so it is not what `check` asks about: the same swap was left open
 * on the Automotive page and has now been answered six times.
 */
export const OUTLOOK_BK_MATURITY = {
  heading: 'How Does Your Digital Maturity Compare?',
  body: 'The Banking Industry Focus cut of the Digital & AI Maturity Report 2026 reveals where companies stand today, and where they need to go. Compare your performance with leaders, average performers, and laggards. Are you leading the shift or reacting to it? Use our data to compare your performance, assess your risks, and find the fastest route to transformation.',
  ctaLabel: 'Explore the Banking Industry Focus report',
  ctaHref: `/en/impact-labs/reports/${DAIMI_BK_SLUG}`,
  /** That report's cover, which this site already holds. */
  photo: {
    src: '/photo/daimi-report-2026-banking-cover.webp',
    alt: 'Digital & AI Maturity Report 2026, Banking Focus',
  } satisfies Photo,
  check:
    'The destination here is decided, not transcribed: this band points at the 2026 report rather than the “2024 Banking Digital Maturity Report” the live page offers, which is what you asked for — so the CTA label and the sentence naming the report are the 2026 cut’s. Worth knowing that the live CTA button actually reads “Explore the 2024 Retail Digital Maturity Report” under a paragraph about the banking one, so the reference was pointing at the wrong sector as well as the wrong year, and it may be worth fixing on digitopia.co too. Three things left to decide. The rest of the paragraph is the live one, so it wants a reading. The heading is the one every outlook page here uses; the live one runs two headings together — “How Does Your Digital Maturity Compare to Industry Leaders? Benchmark Your Digital Maturity” — which reads as a template left half-filled, so say if the longer form is really wanted. And the cover beside it is not on the live page at all: it is here because the other five Future 2030 outlook pages show the maturity cut they point at, and it should come out if this band is meant to stay text-only.',
}

/**
 * The closing band, which is where the live page's gated form sits.
 *
 * Its heading and its sentence are the live band's own — the Retail page closes with
 * an invitation ("Lead the Change") because its live close is headed that way; this
 * one is headed "Download the Full Report", so it is, the same as the Pharmaceutical
 * page's.
 */
export const OUTLOOK_BK_CLOSE = {
  heading: 'Download the Full Report',
  body: 'Learn how top experts are preparing for 2030, and how you can join them. Get detailed projections, DMI trend charts, and strategic insights tailored to banking executives.',
  expertLabel: 'Talk to an expert',
  // `#contact` rather than the Contact Us page: the form sits at the foot of
  // this very page, so the button scrolls instead of navigating away.
  expertHref: '#contact',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy checks
 * in — the same mechanism `outlookRetailChecks()` uses, and appended to the same list
 * in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file. This page
 * arrived with all six of its images. What is left is transcription nobody has read
 * against the original, one figure that does not match the chart, one destination
 * this site cannot offer, two images this page shows that the reference does not, and
 * the gating decision.
 */
export function outlookBankingChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: OUTLOOK_BK_INTRO.check },
    { section: 'outlook', check: OUTLOOK_BK_FIGURE.check },
    { section: 'requirements', check: OUTLOOK_BK_REQUIREMENTS.check },
    { section: 'predictions', check: OUTLOOK_BK_PREDICTIONS.check },
    { section: 'maturity', check: OUTLOOK_BK_MATURITY.check },
    { section: 'download', check: OUTLOOK_BK_CLOSE.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${OUTLOOK_BK_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
